import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_CONNECTORS_CATALOG,
  API_ADMIN_CONNECTORS_LIST,
  API_ADMIN_CONNECTORS_CREATE,
  API_ADMIN_CONNECTORS_UPDATE,
  API_ADMIN_CONNECTORS_DELETE,
  API_ADMIN_CONNECTORS_ACTIVATE,
  API_ADMIN_CONNECTORS_DEACTIVATE,
} from '../../constants/apiConstants';

export interface SysConnectorCatalogDto {
  id: number;
  connectorType: string;
  providerCode: string;
  displayName: string;
  description: string | null;
  baseUrlDefault: string | null;
  authType: string;
  requiredFields: string | null;
  isBuiltIn: boolean;
  isActive: boolean;
}

export interface SysConnectorDto {
  id: number;
  connectorName: string;
  connectorType: string;
  providerCode: string;
  scopePath: string;
  scopeLevel: number;
  baseUrl: string | null;
  authType: string;
  apiKey: string | null;
  extraConfig: string | null;
  /** 'open' | 'assigned' | null — null means access gating not applicable for this connector type. */
  accessMode: string | null;
  isActive: boolean;
  priority: number;
  rateLimitPerMonth: number | null;
  usageCount: number;
  usageResetAt: string | null;
  lastUsedAt: string | null;
  status: string;
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface CreateConnectorRequest {
  connectorName: string;
  connectorType: string;
  providerCode: string;
  scopePath: string;
  baseUrl?: string | null;
  authType: string;
  apiKey?: string | null;
  extraConfig?: string | null;
  accessMode?: string | null;
  isActive?: boolean;
  priority?: number;
  rateLimitPerMonth?: number | null;
}

export interface UpdateConnectorRequest {
  connectorName?: string;
  connectorType?: string;
  providerCode?: string;
  scopePath?: string;
  baseUrl?: string | null;
  authType?: string;
  /** Empty string = preserve existing encrypted value */
  apiKey?: string | null;
  extraConfig?: string | null;
  accessMode?: string | null;
  isActive?: boolean;
  priority?: number;
  rateLimitPerMonth?: number | null;
}

export const connectorService = {
  async getCatalog(): Promise<SysConnectorCatalogDto[]> {
    try {
      const response = await apiClient.get(API_ADMIN_CONNECTORS_CATALOG);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.catalogs ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch connector catalog', error);
      toast.error(`Failed to load connector catalog: ${msg}`);
      throw error;
    }
  },

  async getConnectors(params?: { type?: string; scope?: string }): Promise<SysConnectorDto[]> {
    try {
      const response = await apiClient.get(API_ADMIN_CONNECTORS_LIST, { params });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.connectors ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch connectors', error);
      toast.error(`Failed to load connectors: ${msg}`);
      throw error;
    }
  },

  async createConnector(request: CreateConnectorRequest): Promise<SysConnectorDto> {
    try {
      const response = await apiClient.post(API_ADMIN_CONNECTORS_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Connector created successfully');
      return response.data.data?.connector;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to create connector', error);
      toast.error(`Failed to create connector: ${msg}`);
      throw error;
    }
  },

  async updateConnector(id: number, request: UpdateConnectorRequest): Promise<SysConnectorDto> {
    try {
      const response = await apiClient.put(API_ADMIN_CONNECTORS_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Connector updated successfully');
      return response.data.data?.connector;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to update connector', error, { id });
      toast.error(`Failed to update connector: ${msg}`);
      throw error;
    }
  },

  async deleteConnector(id: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_ADMIN_CONNECTORS_DELETE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Connector deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to delete connector', error, { id });
      toast.error(`Failed to delete connector: ${msg}`);
      throw error;
    }
  },

  async activateConnector(id: number): Promise<number> {
    try {
      const response = await apiClient.patch(API_ADMIN_CONNECTORS_ACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Connector activated');
      return id;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to activate connector', error, { id });
      toast.error(`Failed to activate connector: ${msg}`);
      throw error;
    }
  },

  async deactivateConnector(id: number): Promise<number> {
    try {
      const response = await apiClient.patch(API_ADMIN_CONNECTORS_DEACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Connector deactivated');
      return id;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to deactivate connector', error, { id });
      toast.error(`Failed to deactivate connector: ${msg}`);
      throw error;
    }
  },
};
